// Verification graph for an account-wide FK sweep result: providers as
// swimlanes, segments as nodes, one edge per local-column -> hub match.
// Deterministic grouped layout (no force-directed library) -- the point is
// "does Pathful correctly point at PowerSchool", which a swimlane-per-provider
// layout answers directly, not a general-purpose node-link hairball.
import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';

const CAT_COLORS = [
    'var(--fk-cat-1)', 'var(--fk-cat-2)', 'var(--fk-cat-3)', 'var(--fk-cat-4)',
    'var(--fk-cat-5)', 'var(--fk-cat-6)', 'var(--fk-cat-7)', 'var(--fk-cat-8)',
];

const LANE_WIDTH = 220;
const NODE_HEIGHT = 30;
const NODE_GAP = 10;
const LANE_PADDING_TOP = 48;
const NODE_RADIUS = 6;

const STATUS_STYLE = {
    current: { color: 'var(--fk-status-neutral)', dash: undefined, label: 'Already linked' },
    applied: { color: 'var(--fk-status-good)', dash: undefined, label: 'Applied' },
    queued: { color: 'var(--fk-status-warning)', dash: '5,4', label: 'Needs review' },
};

function buildGraph(current, applied, queued) {
    // One node per (source_title, segment_name) pair, one edge per row.
    const laneOf = new Map();     // source_title -> lane index
    const nodesByLane = new Map(); // source_title -> Map(segment_name -> node)
    const edges = [];

    const ensureNode = (sourceTitle, segmentName) => {
        if (!laneOf.has(sourceTitle)) laneOf.set(sourceTitle, laneOf.size);
        if (!nodesByLane.has(sourceTitle)) nodesByLane.set(sourceTitle, new Map());
        const lane = nodesByLane.get(sourceTitle);
        if (!lane.has(segmentName)) lane.set(segmentName, { segmentName, sourceTitle });
        return lane.get(segmentName);
    };

    const addRow = (row, status) => {
        const local = ensureNode(row.local_source_title || '(unknown)', row.local_segment_name || row.local_actual_name);
        const hub = ensureNode(row.hub_source_title || '(unknown)', row.hub_segment_name);
        edges.push({ from: local, to: hub, status, score: row.score, columnName: row.local_actual_name });
    };
    current.forEach((r) => addRow(r, 'current'));
    applied.forEach((r) => addRow(r, 'applied'));
    queued.forEach((r) => addRow(r, 'queued'));

    // Assign coordinates.
    const lanes = [...laneOf.entries()].sort((a, b) => a[1] - b[1]);
    const positioned = new Map(); // node ref -> {x, y}
    let maxNodesInLane = 0;
    lanes.forEach(([sourceTitle], laneIdx) => {
        const nodes = [...nodesByLane.get(sourceTitle).values()];
        maxNodesInLane = Math.max(maxNodesInLane, nodes.length);
        nodes.forEach((node, i) => {
            positioned.set(node, {
                x: laneIdx * LANE_WIDTH + LANE_WIDTH / 2,
                y: LANE_PADDING_TOP + i * (NODE_HEIGHT + NODE_GAP),
            });
        });
    });

    const width = Math.max(lanes.length * LANE_WIDTH, 240);
    const height = LANE_PADDING_TOP + maxNodesInLane * (NODE_HEIGHT + NODE_GAP) + 20;

    return { lanes, nodesByLane, positioned, edges, width, height };
}

function EdgePath({ from, to, status, score, columnName }) {
    const [hover, setHover] = useState(false);
    const style = STATUS_STYLE[status];
    const dx = Math.max(40, (to.x - from.x) / 2);
    const path = `M ${from.x + LANE_WIDTH / 2 - 8},${from.y + NODE_HEIGHT / 2} ` +
        `C ${from.x + LANE_WIDTH / 2 - 8 + dx},${from.y + NODE_HEIGHT / 2} ` +
        `${to.x - LANE_WIDTH / 2 + 8 - dx},${to.y + NODE_HEIGHT / 2} ` +
        `${to.x - LANE_WIDTH / 2 + 8},${to.y + NODE_HEIGHT / 2}`;
    return (
        <g onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
            <path d={path} className="fk-sweep-edge" stroke={style.color}
                strokeWidth={hover ? 2.5 : 1.5} strokeDasharray={style.dash}
                opacity={hover ? 1 : 0.7} />
            {hover && (
                <title>{typeof score === 'number'
                    ? `${columnName} -> ${style.label} (score ${score})`
                    : `${columnName} -> ${style.label}`}</title>
            )}
        </g>
    );
}

export default function FkGraph({ current, applied, queued, emptyMessage }) {
    const graph = useMemo(
        () => buildGraph(current || [], applied || [], queued || []),
        [current, applied, queued],
    );
    if (graph.edges.length === 0) {
        return emptyMessage ? (
            <Typography className="d-font" sx={{ fontSize: 12, color: 'var(--color-text-muted)', mt: 2 }}>
                {emptyMessage}
            </Typography>
        ) : null;
    }
    const statuses = Object.keys(STATUS_STYLE).filter((k) => graph.edges.some((e) => e.status === k));

    return (
        <Box sx={{ mt: 2 }}>
            <Typography className="d-font" sx={{ fontSize: 12, color: 'var(--color-text-secondary)', mb: 1 }}>
                FK links, grouped by provider (hover an edge for the exact column and score)
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                {statuses.map((key) => {
                    const s = STATUS_STYLE[key];
                    return (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        <span style={{
                            display: 'inline-block', width: 18, height: 0, marginRight: 6,
                            borderTop: `2px ${s.dash ? 'dashed' : 'solid'} ${s.color}`,
                        }} />
                        {s.label}
                    </Box>
                    );
                })}
            </Box>
            <Box className="fk-sweep-graph" sx={{ p: 1 }}>
                <svg width={graph.width} height={graph.height}>
                    {graph.lanes.map(([sourceTitle], laneIdx) => {
                        const color = CAT_COLORS[laneIdx % CAT_COLORS.length];
                        const x = laneIdx * LANE_WIDTH;
                        return (
                            <g key={sourceTitle}>
                                <circle cx={x + 12} cy={20} r={4} fill={color} />
                                <text x={x + 22} y={24} className="fk-sweep-lane-label">{sourceTitle}</text>
                                {[...graph.nodesByLane.get(sourceTitle).values()].map((node) => {
                                    const pos = graph.positioned.get(node);
                                    return (
                                        <g key={node.segmentName}>
                                            <rect x={pos.x - LANE_WIDTH / 2 + 8} y={pos.y}
                                                width={LANE_WIDTH - 16} height={NODE_HEIGHT}
                                                rx={NODE_RADIUS} fill="var(--fk-node-fill)"
                                                stroke={color} strokeWidth={1.5} />
                                            <text x={pos.x} y={pos.y + NODE_HEIGHT / 2 + 4}
                                                textAnchor="middle" className="fk-sweep-node-label">
                                                {node.segmentName.length > 26
                                                    ? `${node.segmentName.slice(0, 24)}…`
                                                    : node.segmentName}
                                            </text>
                                        </g>
                                    );
                                })}
                            </g>
                        );
                    })}
                    {graph.edges.map((e, i) => (
                        <EdgePath key={i} from={graph.positioned.get(e.from)} to={graph.positioned.get(e.to)}
                            status={e.status} score={e.score} columnName={e.columnName} />
                    ))}
                </svg>
            </Box>
        </Box>
    );
}
